import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Descendant } from 'slate'
import { Editable, ReactEditor, RenderElementProps, Slate } from 'slate-react'

import { debounce } from '@/shared/utils'

import { getDefaultDescendantValue } from './utils'

interface SlateEditorProps {
  id?: string
  editor: ReactEditor
  value: Descendant[]
  onChange?: (value: Descendant[]) => void
  placeholder?: string
  renderElement?: (props: RenderElementProps) => JSX.Element
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  style?: React.CSSProperties
}

/**
 * Custom Text Editor used within makyo app builder
 */
export const SlateEditor = ({
  id,
  editor,
  value = getDefaultDescendantValue(''),
  onChange,
  placeholder,
  renderElement,
  onKeyDown,
  style,
}: SlateEditorProps) => {
  const { t } = useTranslation()

  const debouncedOnChange = useCallback(
    (value: Descendant[]) => {
      debounce(() => {
        onChange?.(value)
      }, 200)()
    },
    [onChange]
  )

  return (
    <Slate
      key={id}
      editor={editor}
      value={value}
      onChange={(nextValue) => {
        const isAstChange = editor.operations.some((op) => op.type !== 'set_selection')
        if (isAstChange) {
          debouncedOnChange(nextValue)
        }
      }}
    >
      <Editable
        placeholder={placeholder ?? t('Enter text')}
        renderElement={renderElement}
        onKeyDown={onKeyDown}
        style={style}
      />
    </Slate>
  )
}
